/**
 * markdownToHtml — the project's minimal, dependency-free markdown renderer.
 *
 * ── WHY THIS FILE EXISTS (Phase F1) ────────────────────────────────────────────────────────────
 * This renderer was duplicated verbatim in `InlineEditor.tsx` and `ChatEditor.tsx`. Phase F1 deleted
 * `ChatEditor` and introduced a third preview surface (`FileContentView`'s MD Preview pane), so the
 * choice was between three copies of ~150 lines or one shared module. It was extracted here
 * byte-for-byte — no behavioural change — so every surface renders identically by construction
 * rather than by convention.
 *
 * ── WHO STILL USES IT ──────────────────────────────────────────────────────────────────────────
 * Read-only preview surfaces only — `FileContentView`'s MD Preview pane. `InlineEditor` has since
 * been replaced by `MarkdownLiveEditor`, which does NOT use this renderer: a live-formatted editor
 * styles the markdown source in place and therefore has no HTML representation to generate. That is
 * the point of the split — a viewer converts markdown once for display and has no source to drift
 * from, whereas an editor converting in both directions is where fidelity gets lost.
 *
 * ── WHY NOT A LIBRARY ──────────────────────────────────────────────────────────────────────────
 * The preview path deliberately carries zero markdown dependencies: a ~150-line escape-then-
 * transform renderer is auditable in one sitting, which is what justifies the
 * `dangerouslySetInnerHTML` below. Markdown remains the source of truth and is edited as markdown.
 * (CodeMirror is a dependency of the *editor*, not of this renderer — it parses markdown to style
 * it and never emits HTML.)
 *
 * ── SAFETY ─────────────────────────────────────────────────────────────────────────────────────
 * Output is consumed via `dangerouslySetInnerHTML`. That is acceptable ONLY because the input is
 * HTML-escaped first (see {@link escapeHtml}) before any markdown transform runs, so no caller can
 * inject live markup. Do not reorder those two steps, and do not add a transform that emits
 * attributes interpolated from the source text.
 */

/** Escape HTML before applying markdown transforms — content is trusted (our DB) but we stay safe. */
function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Inline markdown → HTML: code, bold, italic, links. Input is already HTML-escaped.
 *
 * Link colour uses `text-primary-700` + `underline`, not `text-primary`. Raw `--color-primary`
 * (#6a64d7) as *text* on the page background measures ~3.5:1 and fails WCAG 2.1 AA for normal text;
 * `--color-primary-700` (#6d28d9) reaches ~6.4:1. The underline is kept as a second, non-colour cue.
 * This resolves Track 3 open item **T3-2**. Do not revert either half.
 */
function inlineFormat(text: string): string {
  return text
    .replace(
      /`([^`]+)`/g,
      '<code class="rounded bg-subtle-bg px-1 py-0.5 text-sm">$1</code>',
    )
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a class="text-primary-700 underline" href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    );
}

/**
 * Split one markdown table row into trimmed cells, PRESERVING empty cells.
 *
 * Only the leading/trailing pipe delimiters are removed. The previous `.filter((c) => c.trim())`
 * dropped every legitimately blank cell too (e.g. an empty "Notes" column), so a row came out
 * shorter than its header and the remaining values shifted under the wrong column headings.
 */
export function splitTableRow(line: string): string[] {
  let body = line.trim();
  if (body.startsWith("|")) body = body.slice(1);
  if (body.endsWith("|")) body = body.slice(0, -1);
  return body.split("|").map((c) => c.trim());
}

/**
 * Render markdown to HTML. Handles fenced code blocks, headings, horizontal rules, blockquotes,
 * ordered/unordered lists, pipe tables, and paragraphs with inline formatting.
 */
export function markdownToHtml(markdown: string): string {
  const lines = escapeHtml(markdown).replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let i = 0;

  const closeList = (): void => {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line)) {
      closeList();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        code.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      out.push(
        `<pre class="overflow-x-auto rounded-md bg-subtle-bg p-3 text-sm"><code>${code.join("\n")}</code></pre>`,
      );
      continue;
    }

    // Horizontal rule: --- or *** or ___ on its own line
    if (/^([-*_])\1{2,}\s*$/.test(line)) {
      closeList();
      out.push('<hr class="my-4 border-t border-outlines" />');
      i++;
      continue;
    }

    if (/^>\s*/.test(line)) {
      closeList();
      const text = line.replace(/^>\s*/, "");
      out.push(
        `<blockquote class="border-l-4 border-outlines pl-4 italic text-text-secondary my-2">${inlineFormat(text)}</blockquote>`,
      );
      i++;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      closeList();
      const level = heading[1].length;
      out.push(
        `<h${level} class="mb-1 mt-3 font-semibold">${inlineFormat(heading[2])}</h${level}>`,
      );
      i++;
      continue;
    }

    if (/^\s*[-*+]\s+/.test(line)) {
      if (listType !== "ul") {
        closeList();
        out.push('<ul class="ml-5 list-disc">');
        listType = "ul";
      }
      out.push(`<li>${inlineFormat(line.replace(/^\s*[-*+]\s+/, ""))}</li>`);
      i++;
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      if (listType !== "ol") {
        closeList();
        out.push('<ol class="ml-5 list-decimal">');
        listType = "ol";
      }
      out.push(`<li>${inlineFormat(line.replace(/^\s*\d+\.\s+/, ""))}</li>`);
      i++;
      continue;
    }

    if (/^\s*$/.test(line)) {
      closeList();
      i++;
      continue;
    }

    // Table: line starts with | and next line is a separator |---|---|
    if (
      /^\|/.test(line) &&
      i + 1 < lines.length &&
      /^\|[-: |]+\|/.test(lines[i + 1])
    ) {
      closeList();
      const headerCells = splitTableRow(line);
      i += 2; // skip header + separator
      const rows: string[][] = [];
      while (i < lines.length && /^\|/.test(lines[i])) {
        // Pad / truncate to the header width so a short row can never shift data under the wrong
        // column (browsers do not auto-pad missing <td>s).
        const cells = splitTableRow(lines[i]);
        rows.push(
          Array.from(
            { length: headerCells.length },
            (_, idx) => cells[idx] ?? "",
          ),
        );
        i++;
      }
      const thead = `<thead><tr>${headerCells
        .map(
          (c) =>
            `<th class="border border-outlines px-3 py-1 text-left font-semibold">${inlineFormat(c)}</th>`,
        )
        .join("")}</tr></thead>`;
      const tbody = rows
        .map(
          (row) =>
            `<tr>${row
              .map(
                (c) =>
                  `<td class="border border-outlines px-3 py-1">${inlineFormat(c)}</td>`,
              )
              .join("")}</tr>`,
        )
        .join("");
      out.push(
        `<div class="overflow-x-auto my-3"><table class="border-collapse text-sm w-full">${thead}<tbody>${tbody}</tbody></table></div>`,
      );
      continue;
    }

    closeList();
    out.push(`<p class="mb-2 leading-relaxed">${inlineFormat(line)}</p>`);
    i++;
  }

  closeList();
  return out.join("\n");
}

export default markdownToHtml;
